import { size, isFunction, isEmpty, has } from 'lodash';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import React, { useEffect, useState, useCallback, useRef } from 'react';
import InfiniteLoader from 'react-window-infinite-loader';

import AutoSizer from 'react-virtualized-auto-sizer';
import { FixedSizeList, VariableSizeList } from 'react-window';
import { useDidUpdate, usePrevious } from '../../hooks';

const MINIMUM_BATCH_SIZE = 100;
const THRESHOLD_DEFAULT = 10;
const DEFAULT_ITEM_SIZE = 30;

const StyledVirtualizedList = styled(({ component, ...props }) =>
  React.cloneElement(component, { ...props, ref: props.forwardRef })
)`
  ${({ isColumnReverse }) =>
    isColumnReverse &&
    `
    display: flex;
    flex-direction: column-reverse;
  `};

  > div {
    position: ${({ isColumnReverse }) =>
      isColumnReverse ? 'relative' : 'static'};
  }

  ::-webkit-scrollbar {
    width: 4px;
  }

  ::-webkit-scrollbar-thumb {
    background-color: #c2c5cc;
    border-radius: 40px;
  }
`;

const StyledPolygon = styled.div`
  position: absolute;
  width: 100%;
  visibility: hidden;
`;

const PolygonList = React.memo(
  React.forwardRef(({ polygons, itemData, onPolygonsRendered }, outerRef) => {
    const innerRef = useRef();

    const handlePolygonsRendered = useCallback(() => {
      if (innerRef.current) {
        const items = innerRef.current.querySelectorAll('img,svg');
        const itemsLoaderList = [];

        if (document && document.fonts && document.fonts.ready) {
          itemsLoaderList.push(document.fonts.ready);
        }

        if (items && items.length > 0) {
          items.forEach(item => {
            itemsLoaderList.push(
              new Promise((resolve, reject) => {
                if (
                  item.complete ||
                  (item.tagName.toLowerCase() === 'svg' &&
                    item.getBoundingClientRect().width !== 0) ||
                  item.getBoundingClientRect().height !== 0
                ) {
                  resolve();
                } else {
                  item.addEventListener('load', () => resolve());
                  item.addEventListener('error', () => reject());
                }
              })
            );
          });
        }

        Promise.allSettled(itemsLoaderList).then(() => {
          onPolygonsRendered();
        });
      }
    }, [polygons, polygons.length]);

    return (
      <AutoSizer>
        {({ height, width }) => (
          <StyledPolygon
            height={height}
            width={width}
            ref={ref => {
              innerRef.current = ref;
              outerRef.current = ref;
              handlePolygonsRendered();
            }}
          >
            {itemData.map((data, index) =>
              polygons({
                data: itemData,
                index,
              })
            )}
          </StyledPolygon>
        )}
      </AutoSizer>
    );
  })
);

PolygonList.displayName = 'PolygonList';

const InfiniteList = React.forwardRef((props, outerRef) => {
  const {
    isItemLoaded,
    itemCount,
    minimumBatchSize,
    onItemsRendered,
    onLoadRequest,
    threshold,
    flexible,
    children,
    onScroll,
    onScrollTopBorder,
    onScrollBottomBorder,
    isReverse,
    isNextPageLoading,
    itemData,
    onDidUpdateItems,
    ...restProps
  } = props;
  const [itemSizes, setItemSizes] = useState([]);
  const [isStartReverseScrollToBottom, setIsStartReverseScrollToBottom] =
    useState(isReverse);
  const [isReversePaginationStart, setIsReversePaginationStart] =
    useState(false);
  const [isPolygonCalculation, setIsPolygonCalculation] = useState(true);

  const listRef = useRef();
  const innerListRef = useRef();
  const polygonRef = useRef();
  const prevItemSizes = usePrevious(itemSizes);

  const scrollToLastItemReverse = useCallback(() => {
    if (isReverse && listRef.current) {
      let offset = 0;
      for (let i = size(prevItemSizes); i < size(itemSizes); i += 1) {
        offset += itemSizes[i];
      }
      listRef.current.scrollTo(offset);
    }
    setIsReversePaginationStart(false);
  }, [itemSizes, itemSizes.length]);

  const calculateItemSizes = useCallback(() => {
    if (!polygonRef.current) {
      return;
    }

    const polygonItems = polygonRef.current.querySelectorAll(
      `${StyledPolygon} > *`
    );

    if (isEmpty(polygonItems)) {
      return;
    }

    const newItemSizes = [];
    polygonItems.forEach((itemElement, index) => {
      const { scrollHeight } = itemElement;
      newItemSizes[index] = scrollHeight;
    });
    setItemSizes(newItemSizes);
    setIsPolygonCalculation(false);
  }, [polygonRef]);

  const handleScroll = useCallback(
    event => {
      onScroll(event);

      if (!outerRef || !outerRef.current) return;
      const { current: currentOuterRef } = outerRef;

      if (
        event.scrollOffset <= 0 &&
        isReverse &&
        !isStartReverseScrollToBottom
      ) {
        onLoadRequest();
        onScrollTopBorder();
      }

      if (
        currentOuterRef._outerRef &&
        currentOuterRef._outerRef.scrollTop +
          currentOuterRef._outerRef.clientHeight >=
          currentOuterRef._outerRef.scrollHeight - 1
      ) {
        onScrollBottomBorder();
      }
    },
    [isReverse, onLoadRequest, onScroll, isStartReverseScrollToBottom]
  );

  const handleLoadRequest = useCallback(
    (startIndex, stopIndex) => {
      if (!isReverse) {
        onLoadRequest(startIndex, stopIndex);
      }
    },
    [onLoadRequest, isReverse]
  );

  const getItemSize = useCallback(
    index => itemSizes[index] || DEFAULT_ITEM_SIZE,
    [itemSizes, itemSizes.length]
  );

  const checkScrollIsHidden = useCallback(() => {
    if (listRef.current && listRef.current.props) {
      const { props: listRefProps } = listRef.current;
      const sizes = itemSizes.reduce((acc, item) => acc + item, 0);
      return listRefProps.height >= sizes;
    }
    return false;
  }, [itemSizes, itemSizes.length]);

  const handlePolygonsRendered = useCallback(() => {
    calculateItemSizes();

    if (isStartReverseScrollToBottom) {
      const lastMessageIndex = itemData.length - 1;
      listRef.current.scrollToItem(lastMessageIndex, 'end');
      setIsStartReverseScrollToBottom(false);
    }
  }, [itemData, isStartReverseScrollToBottom]);

  const handleDidUpdateItems = useCallback(() => {
    if (isReversePaginationStart) {
      scrollToLastItemReverse();
    }
    onDidUpdateItems();
  }, [onDidUpdateItems, itemSizes, isReversePaginationStart]);

  useEffect(() => {
    if (isReverse && !isNextPageLoading) {
      setIsReversePaginationStart(true);
    }
  }, [isNextPageLoading]);

  useEffect(() => {
    setIsPolygonCalculation(true);
  }, [itemData, itemData.length]);

  useDidUpdate(() => {
    if (listRef.current) {
      handleDidUpdateItems();

      Object.values(itemSizes).forEach((value, index) => {
        const prevValue = prevItemSizes[index];
        if (
          (!prevValue || prevValue !== value) &&
          has(listRef, 'current.resetAfterIndex')
        ) {
          listRef.current.resetAfterIndex(index);
        }
      });
    }
  }, [prevItemSizes, itemSizes, itemSizes.length]);

  const listComponent = flexible ? (
    <VariableSizeList ref={outerRef} />
  ) : (
    <FixedSizeList ref={outerRef} />
  );

  return (
    <>
      {(!flexible || (flexible && itemSizes.length > 0)) && (
        <AutoSizer>
          {({ height, width }) => (
            <InfiniteLoader
              isItemLoaded={isItemLoaded}
              itemCount={itemCount}
              loadMoreItems={handleLoadRequest}
              minimumBatchSize={minimumBatchSize}
              threshold={threshold}
            >
              {({ onItemsRendered: onItemsRenderedInner, ref }) => (
                <StyledVirtualizedList
                  height={height}
                  width={width}
                  itemCount={itemCount}
                  itemSize={getItemSize}
                  component={listComponent}
                  onItemsRendered={innerProps => {
                    const renderProps = { ...innerProps };
                    if (flexible) {
                      renderProps.height = itemSizes.reduce(
                        (acc, item) => acc + item,
                        0
                      );
                    }
                    onItemsRenderedInner(renderProps);
                    onItemsRendered(renderProps);
                  }}
                  threshold={threshold}
                  forwardRef={component => {
                    if (isFunction(outerRef)) {
                      outerRef(component);
                    } else if (outerRef) {
                      outerRef.current = component;
                    }

                    ref(component);
                    listRef.current = component;
                  }}
                  innerRef={innerListRef}
                  onScroll={handleScroll}
                  itemData={itemData}
                  isColumnReverse={isReverse && checkScrollIsHidden()}
                  {...restProps}
                >
                  {children}
                </StyledVirtualizedList>
              )}
            </InfiniteLoader>
          )}
        </AutoSizer>
      )}
      {isPolygonCalculation && flexible && children && (
        <PolygonList
          itemData={itemData}
          polygons={children}
          ref={polygonRef}
          onPolygonsRendered={handlePolygonsRendered}
        />
      )}
    </>
  );
});

InfiniteList.displayName = 'InfiniteList';

InfiniteList.propTypes = {
  isItemLoaded: PropTypes.func,
  itemCount: PropTypes.number.isRequired,
  polygons: PropTypes.arrayOf(PropTypes.node),
  minimumBatchSize: PropTypes.number,
  onLoadRequest: PropTypes.func,
  threshold: PropTypes.number,
  flexible: PropTypes.bool,
  onScrollTopBorder: PropTypes.func,
  onScrollBottomBorder: PropTypes.func,
  onScroll: PropTypes.func,
  isReverse: PropTypes.bool,
  onDidUpdateItems: PropTypes.func,
  onItemsRendered: PropTypes.func,
  children: PropTypes.func.isRequired,
  itemData: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
};

InfiniteList.defaultProps = {
  isItemLoaded: Function.prototype,
  minimumBatchSize: MINIMUM_BATCH_SIZE,
  onLoadRequest: Function.prototype,
  threshold: THRESHOLD_DEFAULT,
  flexible: false,
  polygons: null,
  onScrollTopBorder: Function.prototype,
  onScrollBottomBorder: Function.prototype,
  onScroll: Function.prototype,
  isReverse: false,
  onDidUpdateItems: Function.prototype,
  onItemsRendered: Function.prototype,
};

PolygonList.propTypes = {
  polygons: PropTypes.arrayOf(PropTypes.node).isRequired,
  onPolygonsRendered: PropTypes.func.isRequired,
};

export default InfiniteList;
